import { FC, useContext, useEffect, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Checkbox,
  H2,
  H3,
  Input,
  Link,
  Modal,
  Select,
  Separator,
  Tooltip,
} from '@smartshell/ui-kit';

import { LINKS } from 'links';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import {
  SettingsContext,
  TTelegramNotificationsConfig,
  TTelegramNotificationsTypes,
} from 'settings';
import { ETelegramChannelType, ITelegramChannelId } from 'types';
import { shallowEqual } from 'utils';

import { IBaseModalProps } from '../interfaces';
import {
  IGetTelegramChannelResponse,
  IUpdateTelegramSettingsRequest,
  IUpdateTelegramSettingsResponse,
} from './interfaces';
import { GET_TELEGRAM_CHANNEL, UPDATE_TELEGRAM_SETTINGS } from './schema';
import s from './TelegramModal.module.scss';
import { useTitlesMap } from './UseTitleMap';

export const TelegramModal: FC<IBaseModalProps> = ({ onClose }) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        telegramChannelId,
        telegramNotificationTypes,
        lowStockThreshold,
        telegramToken,
      },
      isLoading,
    },
    actions: { loadSettings },
  } = useContext(SettingsContext);

  console.log(telegramNotificationTypes);
  const [tgToken, setTgToken] = useState<string>('');
  const [currentChannelId, setCurrentChannelId] =
    useState<ITelegramChannelId | null>(telegramChannelId);
  const [state, setState] = useState(telegramNotificationTypes);
  const [error] = useState<string | undefined>(undefined);
  const [errorToken, setErrorToken] = useState<boolean>(false);
  const [count] = useState(lowStockThreshold);
  const [getToken, setGetToken] = useState<boolean>(false);

  useEffect(() => {
    setState(telegramNotificationTypes);
  }, [telegramNotificationTypes]);

  const entries = Object.entries(state);

  const [updateTelegramSettings, { loading: updateSettingsLoading }] =
    useMutation<
      IUpdateTelegramSettingsResponse,
      IUpdateTelegramSettingsRequest
    >(UPDATE_TELEGRAM_SETTINGS, {
      onCompleted: () => {
        loadSettings();
        setGetToken(false);
        notification.info({
          t,
          type: 'success',
          title: t('settings.success'),
          dismiss: 5,
        });
      },
      onError: () => {
        notification.info({
          t,
          type: 'error',
          title: t('errors.updated.title'),
          dismiss: 5,
        });
      },
    });

  const [deleteTelegramSettings, { loading: deleteSettingsLoading }] =
    useMutation<
      IUpdateTelegramSettingsResponse,
      IUpdateTelegramSettingsRequest
    >(UPDATE_TELEGRAM_SETTINGS, {
      onCompleted: () => {
        loadSettings();

        notification.info({
          t,
          type: 'success',
          title: t('settings.success'),
          dismiss: 5,
        });
        onClose();
      },
      onError: () => {
        notification.info({
          t,
          type: 'error',
          title: t('errors.updated.title'),
          dismiss: 5,
        });
        onClose();
      },
    });

  const [getTelegramChannel, { data, loading: getChannelLoading }] =
    useLazyQuery<IGetTelegramChannelResponse>(GET_TELEGRAM_CHANNEL, {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        if (data.getTelegramChannels.length) {
          setCurrentChannelId({
            id: data?.getTelegramChannels[0].id,
            type: ETelegramChannelType.GROUP,
            title: data?.getTelegramChannels[0].title,
          });
        }
        setGetToken(true);
      },
      onError: () => {
        setErrorToken(true);
      },
    });

  const OPTIONS = data?.getTelegramChannels.map((item) => {
    return { value: item.id, title: item.title };
  });

  const isAllSelected = entries.every(([, value]) => value === '1');

  const TITLES_MAP = useTitlesMap();

  useEffect(() => {
    if (telegramToken) {
      setTgToken(telegramToken);
    }
  }, [telegramToken]);

  useEffect(() => {
    if (telegramChannelId?.id) {
      setCurrentChannelId(telegramChannelId);
    }
  }, [telegramChannelId]);

  const getTelegramChannelById = (
    id: string
  ): ITelegramChannelId | undefined => {
    return data?.getTelegramChannels.find((channel) => channel.id === id);
  };

  return (
    <Modal className={s.modal} onClose={onClose}>
      <div className={s.container}>
        <div className={s.header}>
          <H2>{t('integration.telegram')}</H2>
          <H3 className={s.subtitle}>
            {t('integration.telegram.title')}
            <Tooltip
              contentClassName={s.tooltip}
              content={
                <div className={s.tooltip_content}>
                  <H3>{t('integration.telegram.tooltip.title')}</H3>
                  <ul className={s.list}>
                    <li className={s.item}>
                      <span>{t('integration.telegram.tooltip.item_1')} </span>
                      <a
                        className={s.link_bot}
                        target="_blank"
                        href={'https://telegram.me/BotFather'}
                        rel="noreferrer"
                      >
                        @BotFather
                      </a>
                    </li>
                    <li className={s.item}>
                      {t('integration.telegram.tooltip.item_2')}
                    </li>
                    <li className={s.item}>
                      {t('integration.telegram.tooltip.item_3')}
                    </li>
                    <li className={s.item}>
                      {t('integration.telegram.tooltip.item_4')}
                    </li>
                  </ul>
                  <Link
                    className={s.link}
                    title={t('integration.telegram.tooltip.link')}
                    link={LINKS.telegram_notify[lang]}
                  />
                </div>
              }
            />
          </H3>
        </div>
        <div className={s.controls}>
          <div className={s.top}>
            <Input
              value={tgToken}
              onChange={(value) => {
                setErrorToken(false);
                setTgToken(value);
              }}
              title={t('integration.telegram.token')}
              leftContent={<span className={s.minus}>-</span>}
              error={errorToken ? t('errors.error') : undefined}
            />
            <Button
              className={s.top_button}
              disabled={!tgToken || tgToken.includes('*')}
              onClick={() => {
                getTelegramChannel({ variables: { token: tgToken } });
              }}
              isLoading={getChannelLoading}
            >
              {t('integration.telegram.getList')}
            </Button>
          </div>
          <div className={s.bottom}>
            <Select
              disabled={!data}
              name={'telegram_chat'}
              options={OPTIONS || []}
              value={currentChannelId?.title || null}
              onChange={(value) => {
                setCurrentChannelId(
                  getTelegramChannelById(value as string) || telegramChannelId
                );
              }}
              placeholder={t('chat')}
              disableClear={true}
              className={s.select}
            />
            <Input
              variant="secondary"
              placeholder={t('chat.id')}
              disabled={true}
              className={s.bottom_input}
              value={currentChannelId?.id || ''}
              onChange={() => {
                // console.log('sa');
              }}
            />
          </div>
        </div>

        <div className={s.notification_block}>
          <H3>{t('integration.telegram.list.title')}</H3>
          <div className={s.notifications}>
            <Checkbox
              value={isAllSelected}
              className={s.checkbox}
              label={t('setting.notifications.types.all')}
              onChange={() => {
                if (isLoading) return;
                setState(
                  entries.reduce(
                    (acc, value) => ({ ...acc, [value[0]]: '1' }),
                    {}
                  ) as TTelegramNotificationsConfig
                );
              }}
            />
            {entries.map(([type, value]) => {
              const isSelected = value === '1';
              const title = TITLES_MAP[type as TTelegramNotificationsTypes];

              if (!title) return null;

              return (
                <Checkbox
                  key={type}
                  value={isSelected}
                  label={title}
                  onChange={() => {
                    if (isLoading) return;
                    setState({ ...state, [type]: isSelected ? '0' : '1' });
                  }}
                />
              );
            })}
          </div>
        </div>
        <div className={s.footer}>
          <Separator />
          <div className={s.buttons_block}>
            <Button
              onClick={() => {
                deleteTelegramSettings({
                  variables: { input: { token: null } },
                });
              }}
              disabled={!telegramChannelId?.id}
              className={s['link-button']}
              fullWidth
              variant="outline"
              color="red"
              isLoading={deleteSettingsLoading}
            >
              {t('settings.integration.telegramModal.button.delete')}
            </Button>
            <Button
              disabled={
                error !== undefined ||
                isLoading ||
                !currentChannelId?.id ||
                errorToken ||
                (count === lowStockThreshold &&
                  shallowEqual(state, telegramNotificationTypes) &&
                  telegramChannelId?.id === currentChannelId.id &&
                  !getToken)
              }
              isLoading={updateSettingsLoading}
              onClick={() => {
                updateTelegramSettings({
                  variables: {
                    input: {
                      token:
                        !tgToken || tgToken.includes('*') ? undefined : tgToken,
                      chat_id:
                        (telegramChannelId?.id && !tgToken) ||
                        currentChannelId === telegramChannelId
                          ? undefined
                          : String(currentChannelId?.id),
                      notification: JSON.stringify(state),
                    },
                  },
                });
              }}
              fullWidth
            >
              {t('save.settings')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
